import { Icon } from "../components/Common.jsx";
import { fmtPence } from "../lib/utils.js";
import { projectBusyWeekTokens, repriceToCap } from "../lib/spendingDefaults.js";
import { t as ct } from "../lib/content.js";

// One sentence answering the only question the wizard can't otherwise answer at setup time:
// "if my kid has a good week, what does this cost me?"
//
// WHY A LINE AND NOT A PAGE (Alan, 30 Aug 2026). The obvious design is a full review screen
// inside the wizard. It was rejected: at setup time the parent is looking at values THIS
// ENGINE just derived from their own cap, so a review page would be reviewing our own
// homework, on a step that already has skip pressure. The review page earns its keep later,
// after drift — that one lives in Kids ▸ Spending review. Here, a line is enough.
//
// It reads the LIVE values on every render, so a parent who types 60 into a reward sees the
// consequence in the same breath rather than discovering it a fortnight later when their
// child hits the cap on a Tuesday.
// `repriceWhen` (LB-127 PR3): "always" (default — the guided steps, where Reprice is
// offered in every state on purpose) or "offCap" (the setup review, where a freshly
// generated household sums to the cap by construction and a Reprice button would invite a
// parent to fix something that is not broken; it appears only once an edit breaks the fit).
export function EarningsHealthLine({ child, state, update, showToast, repriceWhen = "always" }) {
  if (!child) return null;
  const p = projectBusyWeekTokens(child, state);
  if (p.capTokens === null) return null; // cap off for this child: nothing to measure against

  const vars = {
    name: child.name,
    tok: p.tokens,
    gbp: fmtPence(p.pence),
    cap: fmtPence(p.capPence),
  };
  const roomNote = p.phantom > 0 ? ct("wizard.earn.health_room_note", " (including room for one task)") : "";

  const tone = p.state === "over"
    ? { box: "bg-rose-50 border-rose-200 text-rose-700", icon: <Icon id="ic-attention" size={16} hue="rose" />, btn: "bg-rose-600 text-white" }
    : p.state === "warn"
      ? { box: "bg-amber-50 border-amber-200 text-amber-800", icon: "ℹ", btn: "bg-amber-700 text-white" }
      : { box: "bg-white border-slate-200 text-slate-700", icon: <Icon id="ic-reassure" size={16} hue="teal" />, btn: "bg-transparent text-teal-700 underline underline-offset-2 px-0" };

  const text = p.state === "over"
    ? ct("wizard.earn.health_over", "A busy week for {name} is about {tok} tokens ({gbp}) — over the {cap} cap. The cap will block the extra.", vars)
    : p.state === "warn"
      ? ct("wizard.earn.health_warn", "A busy week for {name} is about {tok} tokens ({gbp}) — well under the {cap} cap. Rewards may feel small.", vars)
      : ct("wizard.earn.health_ok", "A busy week for {name} is about {tok} tokens ({gbp}) of the {cap} cap.", vars) + roomNote;

  // Reprice is visible in EVERY state, not just when something is wrong (Alan, 30 Aug). A
  // button that only appears once a parent has already made a mess is a button they have to
  // discover mid-problem; leaving it in place — quiet when things are fine, loud when they
  // are not — means it is a known control by the time it matters. It scales both ways, so
  // it is also the fix for the warn state, not just the over one.
  // Report what actually happened. The first version said "repriced" unconditionally, which
  // on a household where nothing could move (see repriceToCap's correction-loop note) was
  // simply a lie on screen — the worst possible outcome for a feature whose entire promise
  // is that a parent always knows why a number changed.
  function doReprice() {
    let result = null;
    update((s) => { result = repriceToCap(s, child.id); return result && result.changed ? s : null; });
    if (!showToast || !result) return;
    if (!result.changed) {
      showToast(ct("toast.reprice_nothing", "Nothing to rebalance — everything is already as low as it goes.", { name: child.name }), "info");
    } else if (!result.fits) {
      showToast((result.dropped && result.dropped.length ? ct("toast.reprice_trimmed_over", "Rebalanced and removed {list} — still {over} tokens over the cap. Lower a reward or remove a task to fit.", { list: result.dropped.join(", "), over: result.over }) : ct("toast.reprice_over", "Still {over} tokens over the cap — lower a reward or remove a task to fit.", { over: result.over, name: child.name })), "info");
    } else {
      showToast((result.dropped && result.dropped.length ? ct("toast.reprice_trimmed", "Rebalanced to fit {name}'s cap. Removed {list}", { name: child.name, list: result.dropped.join(", ") }) : ct("toast.repriced", "Rewards rebalanced to fit {name}'s cap", { name: child.name })), "good");
    }
  }

  return (
    <div className={`rounded-lg border px-3.5 py-2.5 flex items-start gap-2.5 ${tone.box}`} data-testid="earnings-health-line" data-health-state={p.state}>
      <span className="text-base leading-none mt-0.5 flex-shrink-0" aria-hidden="true">{tone.icon}</span>
      <p className="text-xs font-semibold leading-snug flex-1 min-w-0">{text}</p>
      {(repriceWhen !== "offCap" || p.state !== "ok") && (
      <button
        type="button"
        onClick={doReprice}
        data-testid="reprice-button"
        className={`text-xs font-extrabold rounded-lg px-2.5 py-1.5 flex-shrink-0 ${tone.btn}`}
      >
        {ct("wizard.earn.reprice", "Rebalance")}
      </button>
      )}
    </div>
  );
}
